import { memo, useState } from 'react';
import { formatTicketNumber } from '@/lib/tickets/ticketDisplayNumber';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { OptiPlaceModal } from '@/components/tickets/OptiPlaceModal';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useShopTime } from '@/hooks/useShopTime';
// DTC-K: route ticket-archive/restore lifecycle write timestamps + the
// OptiPlace appointmentDate fallback through the canonical clock so
// persisted ticket-mutation writes are deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import {
  restoreDeletedTicket,
  didRestore,
  describeRestoreRefusal,
  needsRestoreReload,
} from '@/utils/ticketRestoreLifecycle';
import {
  markCompletedTicketPickedUp,
  isPickupSettled,
  didPickUpNow,
  needsPickupReload,
  describePickupRefusal,
} from '@/utils/completedPickupLifecycle';
import { notifyPickupChanged } from '@/utils/pickupConvergence';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useTicketCustody, useTicketCustodyMutation } from '@/hooks/tickets/useTicketCustody';
import { TicketCustodyBadge } from '@/components/tickets/TicketCustodyBadge';
import {
  Calendar,
  RotateCcw,
  Truck,
  MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Technician {
  id: string;
  name: string;
  is_present: boolean;
}

interface OptiboardTicketRowProps {
  ticket: any;
  tab: 'estimates' | 'scheduled' | 'wip' | 'completed' | 'picked-up' | 'deleted';
  technicians: Technician[];
  onRefresh: () => void;
  showStatus?: boolean;
  statusLabel?: string;
}

// IV-D-28 R3 FIX-3: wrap in React.memo to prevent re-renders when parent state
// changes don't affect this row's props. All props are referentially stable:
// `ticket` + `technicians` come from React Query (stable until invalidated);
// `onRefresh` is React Query's stable refetch; `tab`/`showStatus`/`statusLabel`
// are primitives. Safe shallow-equal semantics.
function OptiboardTicketRowInner({ ticket, tab, technicians, onRefresh, showStatus, statusLabel }: OptiboardTicketRowProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { formatTimeInShopTZ } = useShopTime();
  const [isOptiPlaceOpen, setIsOptiPlaceOpen] = useState(false);
  // Lifecycle v2 — slice 3.6. Flag-gated custody chip, same query as
  // TicketCard so React Query dedupes when both surfaces are mounted.
  // Flag-off: query disabled, badge not rendered, zero network.
  const custodyHandoffEnabled = useFeatureFlag('custodyHandoff');
  const { data: custodyRow } = useTicketCustody(
    custodyHandoffEnabled ? (ticket.id as string) : null,
  );
  // Slice 4C.1: clickable BOH-return pill on Optiboard rows.
  const optiboardCustodyMutation = useTicketCustodyMutation();
  const handleAcceptBackFromOptiboard = () => {
    if (!custodyHandoffEnabled) return;
    optiboardCustodyMutation
      .mutateAsync({
        ticketId: ticket.id as string,
        eventKind: 'boh_accept',
        reason: null,
      })
      .catch(() => {
        /* mutation hook surfaces toast */
      });
  };

  const vehicle = ticket.vehicle;
  const vehicleDisplay = vehicle 
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` 
    : '—';

  const techDisplay = ticket.technician?.name || '—';
  const useParkingSpot = tab === 'scheduled' || tab === 'completed' || tab === 'picked-up';
  const locationDisplay = useParkingSpot
    ? (ticket.parking_spot?.label || '—')
    : (ticket.bay?.name || '—');
  
  // Format scheduled_date using shop timezone (matches Dayboard)
  const getScheduledDisplay = () => {
    if (!ticket.scheduled_date) return '—';
    try {
      // scheduled_date is YYYY-MM-DD format, parse it as local date
      const [year, month, day] = ticket.scheduled_date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString();
    } catch {
      return ticket.scheduled_date;
    }
  };

  // Format start_time or picked_up_at using shop timezone
  const getStartTimeDisplay = () => {
    if (tab === 'picked-up') {
      if (!ticket.picked_up_at) return '—';
      try {
        return formatTimeInShopTZ(new Date(ticket.picked_up_at));
      } catch {
        return '—';
      }
    }
    if (!ticket.start_time) return '—';
    try {
      const utcDate = new Date(ticket.start_time);
      return formatTimeInShopTZ(utcDate);
    } catch {
      return '—';
    }
  };

  // Jobs display logic
  const jobs = ticket.ticket_jobs || [];
  const jobNames = jobs.map((tj: any) => tj.job?.jobs_catalog?.label || tj.job?.name || 'Unknown Job');
  const getJobsDisplay = () => {
    if (jobNames.length === 0) {
      return <span className="text-muted-foreground">No jobs</span>;
    }
    const displayJobs = jobNames.slice(0, 2).join(', ');
    const moreCount = jobNames.length - 2;
    return (
      <span title={jobNames.join(', ')} className="cursor-default">
        {displayJobs}{moreCount > 0 && <span className="text-muted-foreground"> (+{moreCount})</span>}
      </span>
    );
  };

  // Timer display logic
  const getTimerDisplay = () => {
    if (ticket.timer_status === 'running') {
      return <Badge variant="default" className="bg-green-500">Running</Badge>;
    }
    if (ticket.timer_status === 'paused') {
      return <Badge variant="secondary">Paused</Badge>;
    }
    if (ticket.actual_minutes) {
      return `${ticket.actual_minutes} min`;
    }
    return '—';
  };

  const bookTimeDisplay = ticket.predicted_minutes 
    ? `${ticket.predicted_minutes} min` 
    : '—';

  // Handle Convert/Schedule action for estimates
  const handleConvert = () => {
    setIsOptiPlaceOpen(true);
  };

  // 8G.2C.5: routed through the SERVER completed-pickup authority.
  //
  // This used to call `move_vehicle_location(..., p_is_pickup: true)`. That RPC
  // is SECURITY DEFINER, so it ran as `postgres` and the direct-DML guards let
  // it through by design — they exist to stop a browser writing terminal facts.
  // MEASURED as a real manager, it succeeded on an Active ticket with a RUNNING
  // timer, on a paused episode, and on one still owing a review. The button
  // living on the Completed tab was the only thing standing between an advisor
  // and handing back a car a technician was still working on.
  const { canReleaseCompletedVehicle } = usePermissions();

  const handleMarkPickedUp = async () => {
    const pickup = await markCompletedTicketPickedUp(ticket.id);

    // Unknown result: the handover may or may not have committed. Reload rather
    // than replay — a customer either drove away or did not, and re-firing
    // would risk recording a second departure for one car.
    if (needsPickupReload(pickup.outcome)) {
      toast({
        title: 'Pickup result unconfirmed',
        description: describePickupRefusal(pickup),
        variant: 'destructive',
      });
      onRefresh();
      return;
    }

    if (!isPickupSettled(pickup.outcome)) {
      toast({
        title: 'Cannot mark as picked up',
        description: describePickupRefusal(pickup),
        variant: 'destructive',
      });
      return;
    }

    // OCIS-R69-FIX (HIGH-5): the authority records the physical departure but
    // emits no timeline event, so the client writes one.
    //
    // 8G.2C.5 gates it on `didPickUpNow`, NOT on "settled". ALREADY_PICKED_UP
    // means the car left earlier — a retry after a lost response, or a
    // colleague who got there first — and logging a second human pickup event
    // for one departure would put an event in the timeline that never happened.
    //
    // This remains a client-derived record and is NOT exactly-once under a lost
    // response: the server state is authoritative, the event is derived, and
    // consolidating the two is 8G.3.
    if (didPickUpNow(pickup.outcome) && pickup.shopId && user?.id) {
      logTicketEventToOcis({
        ticketId: ticket.id,
        shopId: pickup.shopId,
        eventType: 'ticket_status_changed',
        content: 'Vehicle picked up by customer',
        authorUserId: user.id,
        payload: {
          reason: 'vehicle_picked_up',
          picked_up: true,
          picked_up_at: pickup.pickedUpAt,
        },
      }).catch(err => console.error('[OptiboardTicketRow] Pickup OCIS log error:', err));
    }

    // Every mounted surface, here and on the shop's other devices, re-reads
    // (FOH queue, Dayboard, Calendar). New pickups only.
    notifyPickupChanged({
      isNewPickup: didPickUpNow(pickup.outcome), ticketId: ticket.id,
      shopId: pickup.shopId, pickedUpAt: pickup.pickedUpAt, act: 'completed',
    });

    toast({
      title: didPickUpNow(pickup.outcome) ? 'Marked as Picked Up' : 'Already picked up',
      description: didPickUpNow(pickup.outcome)
        ? undefined
        : 'This vehicle had already been handed back. The board has been refreshed.',
    });
    onRefresh();
  };

  // Handle Restore
  // 8G.2C.3: routed through the SERVER restore authority. The legacy
  // transition fabricated a technician pause (status='Paused',
  // timer_status='paused', paused_at, paused_by='user') for a person who never
  // paused, and assumed a Deleted row's sessions were already closed — an
  // assumption 8G.2 removed when deletion stopped force-closing them.
  const handleRestore = async () => {
    const restored = await restoreDeletedTicket(ticket.id, 'Restored from Optiboard');

    // 8G.2C.4 §16/§17: an unconfirmed result is NOT a refusal and NOT a
    // success. The transaction may have committed, so do not re-fire the human
    // Restore act; the canonical attribution is missing, so do not emit a
    // downstream record built from it either. Reload and let the operator see
    // the real state.
    if (needsRestoreReload(restored.outcome)) {
      toast({
        title: 'Restore result unconfirmed',
        description: describeRestoreRefusal(restored),
        variant: 'destructive',
      });
      onRefresh();
      return;
    }

    if (!didRestore(restored.outcome)) {
      toast({
        title: restored.outcome === 'TERMINAL_EVIDENCE_CONFLICT'
          ? 'Unresolved technician work'
          : 'Cannot restore',
        description: describeRestoreRefusal(restored),
        variant: 'destructive',
      });
    } else {
      // Log pensieve event with the CANONICAL shop, resolved server-side
      // under the transition lock rather than read from React context.
      const restoreShopId = restored.shopId;
      if (restoreShopId && user?.id) {
        await supabase.from('pensieve_events').insert({
          shop_id: restoreShopId,
          ticket_id: ticket.id,
          event_type: 'ticket.restored',
          session_id: crypto.randomUUID(),
          timestamp: clock.now().toISOString(),
          metadata: {
            restored_to: 'parking_lot',
            performed_by_user_id: user.id,
          },
        });

        // OCIS-R69-FIX (HIGH-3): restore previously wrote Pensieve only —
        // the restored ticket silently re-emerged with no timeline event.
        logTicketEventToOcis({
          ticketId: ticket.id,
          shopId: restoreShopId,
          eventType: 'ticket_status_changed',
          content: 'Ticket restored from deleted → general lot (active, unassigned)',
          authorUserId: user.id,
          payload: {
            fromStatus: 'Deleted',
            toStatus: restored.status ?? 'Active',
            reason: 'restored_from_deleted',
            restored_to: 'parking_lot',
          },
        }).catch(err => console.error('[OptiboardTicketRow] Restore OCIS log error:', err));
      }
      toast({ title: 'Ticket Restored', description: 'Moved to parking lot' });
      onRefresh();
    }
  };

  // Core-100 R4 (CORE-AS): no Archive here. OptiBoard "MUST NOT decide anything"; the old button was a raw browser
  // UPDATE of tickets.archived_at that no board predicate even read. Deleted work stays visible AS deleted (to
  // managers and owners, OD-17); restoring goes through the owning lifecycle (ticketRestoreLifecycle).

  // Prepare ticket data for OptiPlace conversion
  const getOptiPlaceData = () => ({
    id: ticket.id,
    vehicle: vehicle ? {
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      engineSize: vehicle.engine_size,
      vin: vehicle.vin,
      licensePlate: vehicle.license_plate,
    } : undefined,
    vehicleId: ticket.vehicle_id,
    customerName: ticket.customer_name,
    customerPhone: ticket.customer_phone,
    type: ticket.type || 'Dropoff',
    priority: ticket.priority || 'Normal',
    appointmentDate: clock.now().toISOString(), // Default to today, OptiPlace will use selected date
    isComeback: ticket.is_comeback || false,
    predictedMinutes: ticket.predicted_minutes || 0,
    jobs: [], // Will be populated by OptiPlaceModal from ticket_jobs
    isEstimateConversion: true, // Flag for estimate conversion mode
  });

  // Render action buttons based on tab
  const renderActions = () => {
    switch (tab) {
      case 'estimates':
        return (
          <Button size="sm" onClick={handleConvert} className="gap-1">
            <Calendar className="h-3 w-3" />
            Convert / Schedule
          </Button>
        );
      
      case 'scheduled':
      case 'wip':
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/?tab=dayboard&highlight=${ticket.id}`)}>
                View on Dayboard
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      
      case 'completed':
        // 8G.2C.7: this button rendered for EVERY role. The server refuses a
        // TECHNICIAN, PARTS or AUDITOR caller, so nothing could be written —
        // but offering an action that can only fail is a lie about what the
        // page can do, and it is the same defect §9 of 8G.2C.3 fixed for the
        // Restore button. Gated on its OWN capability, not a borrowed one.
        return canReleaseCompletedVehicle ? (
          <Button size="sm" variant="outline" onClick={handleMarkPickedUp} className="gap-1">
            <Truck className="h-3 w-3" />
            Mark Picked Up
          </Button>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        );
      
      case 'picked-up':
        return <span className="text-muted-foreground text-sm">—</span>;
      
      case 'deleted':
        return (
          <div className="flex gap-2">
            {/* 8G.2C.3 §9: the deleted tab is keyed on the deletion tombstone,
                which a CANCELLED visit also carries since 8G.2B. Cancellation
                releases chain lineage, so a restore would rebuild an orphan
                rather than the visit — the authority refuses it with
                UNSUPPORTED_TERMINAL_STATE. Don't offer the action here either;
                the two surfaces must not disagree about what is possible. */}
            {ticket.status === 'Cancelled' ? (
              <span className="self-center text-xs text-muted-foreground">
                Cancelled — can’t be restored
              </span>
            ) : (
              <Button size="sm" variant="outline" onClick={handleRestore}>
                <RotateCcw className="h-3 w-3 mr-1" />
                Restore
              </Button>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  const getStatusBadgeVariant = (tab: string) => {
    switch (tab) {
      case 'estimates': return 'secondary';
      case 'scheduled': return 'outline';
      case 'wip': return 'default';
      case 'completed': return 'default';
      case 'picked-up': return 'secondary';
      case 'deleted': return 'destructive';
      default: return 'secondary';
    }
  };

  // Get border color class based on timer status (matches Dayboard TicketCard pattern)
  const getRowBorderClass = () => {
    if (ticket.timer_status === 'running') {
      return 'border-green-500';
    }
    if (ticket.timer_status === 'paused') {
      return 'border-yellow-600';
    }
    return 'border-border';
  };

  return (
    <>
      <tr className="group">
        <td colSpan={showStatus ? 12 : 11} className="p-0 pb-2">
          <div className={cn(
            "flex items-center rounded-lg bg-card border-2 transition-colors hover:bg-muted/50",
            getRowBorderClass()
          )}>
            {showStatus && (
              <div className="px-4 py-3 text-sm min-w-[100px]">
                <Badge variant={getStatusBadgeVariant(tab) as any} className={tab === 'wip' ? 'bg-green-500' : tab === 'completed' ? 'bg-blue-500' : ''}>
                  {statusLabel || tab}
                </Badge>
              </div>
            )}
<div className="px-4 py-3 font-mono text-sm min-w-[120px]">
              {/* NOV3 A6: one repair = one number */}
              {formatTicketNumber(ticket as any)}
            </div>
            <div className="px-4 py-3 text-sm min-w-[160px]">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span>{ticket.customer_name || '—'}</span>
                {custodyHandoffEnabled && (
                  <TicketCustodyBadge
                    custody={custodyRow ?? null}
                    onAcceptBoh={handleAcceptBackFromOptiboard}
                  />
                )}
              </div>
              {ticket.customer_phone && (
                <div className="text-xs text-muted-foreground">{ticket.customer_phone}</div>
              )}
            </div>
            <div className="px-4 py-3 text-sm min-w-[180px]">{vehicleDisplay}</div>
            <div className="px-4 py-3 text-sm min-w-[180px] flex-1">{getJobsDisplay()}</div>
            <div className="px-4 py-3 text-sm min-w-[120px]">{techDisplay}</div>
            <div className="px-4 py-3 text-sm min-w-[100px]">{locationDisplay}</div>
            <div className="px-4 py-3 text-sm min-w-[80px]">{getStartTimeDisplay()}</div>
            <div className="px-4 py-3 text-sm min-w-[100px]">{getScheduledDisplay()}</div>
            <div className="px-4 py-3 text-sm min-w-[90px]">{getTimerDisplay()}</div>
            <div className="px-4 py-3 text-sm min-w-[90px]">{bookTimeDisplay}</div>
            <div className="px-4 py-3 min-w-[160px]">{renderActions()}</div>
          </div>
        </td>
      </tr>

      {/* OptiPlace Modal for estimate conversion */}
      {isOptiPlaceOpen && (
        <OptiPlaceModal
          isOpen={isOptiPlaceOpen}
          onClose={() => setIsOptiPlaceOpen(false)}
          ticketData={getOptiPlaceData()}
          onAssignmentComplete={(ticketId) => {
            setIsOptiPlaceOpen(false);
            toast({ title: 'Estimate Converted', description: 'Ticket is now scheduled' });
            onRefresh();
          }}
        />
      )}

    </>
  );
}

// IV-D-28 R3 FIX-3: memo wrap — see comment at OptiboardTicketRowInner.
export const OptiboardTicketRow = memo(OptiboardTicketRowInner);
